import { FC, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Typography,
  Stack,
  LinearProgress,
  AlertColor,
} from "@mui/material";
import { Add, FileDownload } from "@mui/icons-material";
import { DragNDropFile } from "components/DragNDropFile";
import { AddFileButton } from "components/AddFileButton";
import CustomTable from "components/Table/CustomTable";
import { UPLOAD_FILE_MODAL } from "helpers/constants";
import { MarkdownStyleContainer } from "components/Container/MarkdownStyleContainer";
import Markdown from "react-markdown";
import { useAddStudentsStep } from "../hooks/useAddStudentsStep";
import { useClinicText } from "hooks/useClinicText";
import { Organization } from "types/organization";
import { AddStudentModal } from "../components/AddStudentModal";

type AddStudentsStepProps = {
  onNext?: () => void;
  onBack?: () => void;
  graduationClassId?: string;
  organization?: Organization;
  onGraduationClassStudentsAdded?: (students: string[]) => void;
};

// ReportAlert component inline to avoid external dependencies
type ReportAlertProps = {
  report: string[];
  severity: AlertColor;
  textDescription: string;
};

const ReportAlert: FC<ReportAlertProps> = ({
  report,
  severity,
  textDescription,
}) => {
  const [displayList, setDisplayList] = useState(false);
  return (
    <Box>
      <Alert severity={severity}>
        <Box>
          {report.length} {textDescription}
        </Box>
        <Box mt={1}>
          <Button
            color={severity}
            variant="outlined"
            size="small"
            onClick={() => setDisplayList(!displayList)}
          >
            {displayList ? "Hide" : "Show"} list
          </Button>
        </Box>
        {displayList && (
          <Box pt={1}>
            {report.map((email) => (
              <Box key={email}>{email}</Box>
            ))}
          </Box>
        )}
      </Alert>
    </Box>
  );
};

export const AddStudentsStep: FC<AddStudentsStepProps> = ({
  onNext,
  onBack,
  graduationClassId,
  organization,
  onGraduationClassStudentsAdded,
}) => {
  const {
    completed,
    data,
    displayData,
    error,
    file,
    loading,
    loadingData,
    report,
    selectedData,
    setFile,
    handleDownloadClick,
    handleContinue,
    onSubmit,
    resetForm,
    isAddStudentModalOpen,
    handleOpenAddStudentModal,
    handleCloseAddStudentModal,
    handleAddStudent,
    handleAddFromCSV,
  } = useAddStudentsStep({
    graduationClassId,
    onGraduationClassStudentsAdded,
  });

  const { textGraduationClass } = useClinicText(false, organization);

  const handleCompleteSetup = async () => {
    await onSubmit();
  };

  const handleBackToUpload = () => {
    resetForm();
  };

  const handleFinish = () => {
    if (onNext) {
      onNext();
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 1 }}>
          Add Students
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          {displayData && (
            <AddFileButton
              maxFileSizeMB={1}
              allowedExtensions={["csv"]}
              onFileUploaded={handleAddFromCSV}
              disabled={loading}
              size="small"
              variant="outlined"
            >
              Add From CSV
            </AddFileButton>
          )}
          <Button
            variant="contained"
            onClick={handleOpenAddStudentModal}
            disabled={loading}
            size="small"
            startIcon={<Add />}
          >
            Add Students
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
        Upload a CSV file with student data to add them to the{" "}
        {textGraduationClass.toLowerCase()} or add them manually.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Initial state - File upload */}
      {!displayData && !loading && !completed && (
        <Box>
          <Box mt={2}>
            <DragNDropFile
              maxFileSizeMB={1}
              allowedExtensions={["csv"]}
              onFileUploaded={(file) => {
                setFile(file);
              }}
            />
          </Box>
          <Box mt={2}>
            <Stack direction="row" justifyContent="space-between">
              <Typography component={"div"} variant="h6">
                {UPLOAD_FILE_MODAL.TEMPLATE_EXAMPLE}
              </Typography>
              <Button
                variant="text"
                color="primary"
                onClick={handleDownloadClick}
                startIcon={<FileDownload />}
                size="small"
              >
                Download Template
              </Button>
            </Stack>
            <Box
              px={2}
              py={1}
              mt={1}
              sx={{
                backgroundColor: "rgba(25, 118, 210, 0.08)",
                border: "1px solid rgba(25, 118, 210, 0.3)",
                borderRadius: 1,
              }}
            >
              <MarkdownStyleContainer>
                <Markdown>
                  {UPLOAD_FILE_MODAL.TEMPLATE_EXAMPLE_DESCRIPTION}
                </Markdown>
              </MarkdownStyleContainer>
            </Box>
          </Box>
        </Box>
      )}

      {/* Data preview - Table with students */}
      {displayData && !loading && !completed && (
        <Box mt={2}>
          <Typography variant="h6" sx={{ mt: 3 }}>
            Student Preview
          </Typography>
          <CustomTable
            entities={data}
            columns={[
              {
                id: "name",
                label: "Name",
                value: "name",
              },
              {
                id: "email",
                label: "Email",
                value: "email",
              },
            ]}
            handleActionClick={() => {}}
            fieldToFilter={"name"}
            isSelectable={true}
            extraActionItems={[]}
            idColumn={"email"}
            entitiesIdsAlreadySelected={selectedData.current}
            onSelectionChanged={(selected) => {
              selectedData.current = selected;
            }}
          />
        </Box>
      )}

      {/* Loading state */}
      {loading && (
        <Box mt={2} mb={4} maxWidth={720} sx={{ mx: "auto" }}>
          <Typography variant="body1" align="center" sx={{ mb: 2 }}>
            Uploading students{" "}
            {~~((loadingData.current / loadingData.total) * 100)}%
          </Typography>
          <Box sx={{ width: "100%" }}>
            <LinearProgress
              variant="determinate"
              value={(loadingData.current / loadingData.total) * 100}
            />
          </Box>
        </Box>
      )}

      {/* Completed state - Report */}
      {completed && (
        <Box mt={2}>
          <Typography variant="h6" sx={{ mb: 2, color: "success.main" }}>
            Students added successfully!
          </Typography>
          <Stack spacing={2}>
            {report.success.length > 0 && (
              <ReportAlert
                report={report.success}
                severity="success"
                textDescription="new students added successfully"
              />
            )}
            {report.alreadyExists.length > 0 && (
              <ReportAlert
                report={report.alreadyExists}
                severity="warning"
                textDescription="students already existed in the system"
              />
            )}
            {report.error.length > 0 && (
              <ReportAlert
                report={report.error}
                severity="error"
                textDescription="students could not be created"
              />
            )}
          </Stack>
        </Box>
      )}

      {/* Action buttons */}
      <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
        <Box>
          {onBack && (
            <Button variant="outlined" onClick={onBack} disabled={loading}>
              Back
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* Initial state buttons */}
          {!displayData && !loading && !completed && (
            <>
              {onNext && (
                <Button
                  variant="text"
                  color="primary"
                  onClick={onNext}
                  disabled={loading}
                >
                  Skip this step and continue
                </Button>
              )}
              <Button
                disabled={!file}
                onClick={() => handleContinue(file as File)}
                variant="contained"
                color="primary"
              >
                Continue
              </Button>
            </>
          )}

          {/* Data preview buttons */}
          {displayData && !loading && !completed && (
            <>
              <Button
                variant="outlined"
                onClick={handleBackToUpload}
                disabled={loading}
              >
                Clear All
              </Button>
              {onNext && (
                <Button
                  variant="text"
                  color="primary"
                  onClick={onNext}
                  disabled={loading}
                >
                  Skip this step and continue
                </Button>
              )}
              <Button
                disabled={loading || selectedData.current.length === 0}
                onClick={handleCompleteSetup}
                variant="contained"
                color="primary"
              >
                Complete Setup
              </Button>
            </>
          )}

          {/* Completed state buttons */}
          {completed && (
            <Button onClick={handleFinish} variant="contained" color="primary">
              Finish
            </Button>
          )}
        </Box>
      </Box>

      {/* Add Student Modal */}
      <AddStudentModal
        open={isAddStudentModalOpen}
        onClose={handleCloseAddStudentModal}
        onAdd={handleAddStudent}
      />
    </Box>
  );
};
